/**
 * API Cache utility for deduplicating and caching API requests
 * - Errors are NOT cached (only successful responses)
 * - POST/PUT/DELETE methods are NEVER cached (only deduplicated)
 *
 * Usage:
 *   import { createCachedRequest, apiCache } from 'lib/apiCache';
 *
 *   // Create a cached request function
 *   const fetchUsers = createCachedRequest(
 *     (userId) => makeRequest("GET", `/api/users/${userId}`),
 *     { ttl: 30000, key: 'users' }  // Cache for 30 seconds
 *   );
 *
 *   // Will only make one request even if called multiple times with same args
 *   const users = await fetchUsers(123);
 *
 *   // Clear cache when needed
 *   fetchUsers.clearCache();
 *
 *   // Or clear all cache
 *   apiCache.clear();
 */

const cache = new Map();
const pendingRequests = new Map();
const lastRequestTime = new Map();

let requestCounter = 0;
let getUserId = () => null;

export const configureUserIdGetter = (getter) => {
    getUserId = getter;
};

export function createCachedRequest(fetcher, options = {}) {
    const { ttl = 0, debounceMs = 1000, key = `request_${++requestCounter}`, userAware = false, method = 'GET', maxEntries = 50 } = options;

    const shouldCache = method === 'GET';

    const cachedFetcher = async (...args) => {
        let cacheKey = key + JSON.stringify(args);
        if (userAware) {
            const userId = getUserId();
            cacheKey = userId ? `${userId}:${cacheKey}` : cacheKey;
        }

        const now = Date.now();

        if (shouldCache) {
            const cached = cache.get(cacheKey);
            if (cached) {
                const isExpired = ttl > 0 && now - cached.timestamp > ttl;
                if (!isExpired) {
                    return cached.data;
                }
                cache.delete(cacheKey);
            }
        }

        const lastTime = lastRequestTime.get(cacheKey) || 0;
        if (now - lastTime < debounceMs) {
            const pending = pendingRequests.get(cacheKey);
            if (pending) {
                return pending;
            }
        }

        const existingRequest = pendingRequests.get(cacheKey);
        if (existingRequest) {
            return existingRequest;
        }

        lastRequestTime.set(cacheKey, now);

        const requestPromise = (async () => {
            try {
                const result = await fetcher(...args);
                if (shouldCache) {
                    const existingKeys = Array.from(cache.keys()).filter((k) => k.includes(key));
                    if (existingKeys.length >= maxEntries) {
                        const oldestKey = existingKeys[0];
                        cache.delete(oldestKey);
                        lastRequestTime.delete(oldestKey);
                    }

                    cache.set(cacheKey, { data: result, timestamp: Date.now() });
                }

                return result;
            } catch (error) {
                console.error(`[API Cache] Request failed for ${key}, not caching`);
                throw error;
            } finally {
                setTimeout(() => {
                    pendingRequests.delete(cacheKey);
                }, 100);
            }
        })();

        pendingRequests.set(cacheKey, requestPromise);
        return requestPromise;
    };

    cachedFetcher.clearCache = () => {
        const keysToDelete = Array.from(cache.keys()).filter((k) => k.includes(key));
        keysToDelete.forEach((k) => {
            cache.delete(k);
            lastRequestTime.delete(k);
            pendingRequests.delete(k);
        });
    };

    cachedFetcher.invalidate = () => {
        const keysToDelete = Array.from(cache.keys()).filter((k) => k.includes(key));
        keysToDelete.forEach((k) => {
            cache.delete(k);
            lastRequestTime.delete(k);
        });
    };

    cachedFetcher.getCacheInfo = () => {
        const relevantKeys = Array.from(cache.keys()).filter((k) => k.includes(key));
        return {
            size: relevantKeys.length,
            keys: relevantKeys,
        };
    };

    return cachedFetcher;
}

export function dedupe(fetcher, getKey = () => 'default') {
    const pending = new Map();

    return async (...args) => {
        const key = getKey(...args);

        const existing = pending.get(key);
        if (existing) {
            return existing;
        }

        const promise = (async () => {
            try {
                return await fetcher(...args);
            } finally {
                pending.delete(key);
            }
        })();

        pending.set(key, promise);
        return promise;
    };
}

export function debounceRequest(fetcher, debounceMs = 1000) {
    let lastCallTime = 0;
    let lastResult = null;
    let pending = null;

    return async (...args) => {
        const now = Date.now();
        if (now - lastCallTime < debounceMs && lastResult !== null) {
            return lastResult;
        }

        if (pending) {
            return pending;
        }

        lastCallTime = now;

        pending = (async () => {
            try {
                const result = await fetcher(...args);
                lastResult = result;
                return result;
            } finally {
                pending = null;
            }
        })();

        return pending;
    };
}

export const apiCache = {
    clear: () => {
        cache.clear();
        pendingRequests.clear();
        lastRequestTime.clear();
    },
    size: () => cache.size,
    keys: () => Array.from(cache.keys()),
    has: (key) => {
        return Array.from(cache.keys()).some((k) => k.includes(key));
    },

    clearUserCache: () => {
        const userId = getUserId();
        if (!userId) return;

        const keysToDelete = Array.from(cache.keys()).filter((k) => k.startsWith(`${userId}:`));
        keysToDelete.forEach((k) => {
            cache.delete(k);
            lastRequestTime.delete(k);
        });
    },

    getStats: () => ({
        totalEntries: cache.size,
        pendingRequests: pendingRequests.size,
        keys: Array.from(cache.keys()),
    }),
};

export default apiCache;
